import { rollCredits } from '../credits';
import { formatGold, formatPlayClock } from '../format';
import { icon } from '../icons';
import type { ScreenMount } from '../types';
import { buildDamageRecap } from './damageRecap';
import { getLeaderboard } from '../leaderboardApi';
import { formatChrono, openPseudoDialog } from './leaderboard';

/**
 * Écran de **victoire** (phase 5, A78, A82) — phase `victory`.
 *
 * Après l'Invasion du niveau 10 (le Tricorne), à **chaque** victoire à ce
 * niveau ; ou à la reprise d'une partie déjà en mode infini qui n'a jamais vu
 * les crédits (**victoire différée**, sous-titre adapté). Bandeau d'or, bilan
 * de la partie en tuiles, récapitulatif des dégâts replié, bouton
 * « Continuer : mode infini » (`acknowledgeVictory()`) et bouton « Crédits »
 * qui rejoue le défilement.
 *
 * **Crédits enchaînés** : la première fois sur la sauvegarde
 * (`stats.creditsSeen` faux), ils défilent d'eux-mêmes ≈ 1,2 s après le
 * montage ; « Continuer » attend leur fin (ou le toucher qui les passe).
 * Aucune info-bulle (phase 4) sur cet écran.
 */

/** Délai avant le défilement automatique des crédits. */
export const CREDITS_AUTO_DELAY_MS = 1_200;

/** Niveau du Tricorne : au-delà, la victoire affichée est différée. */
const LAST_CAMPAIGN_LEVEL = 10;

export const mountVictory: ScreenMount = (container, ctx) => {
  const { game } = ctx;
  const s = game.state;
  const st = s.stats;
  const deferred = s.level > LAST_CAMPAIGN_LEVEL;
  // A94 (v0.46.0) : la première victoire annonce ce que le mode infini ouvre ;
  // la victoire différée (partie reprise déjà en infini) garde son texte.
  const lead = deferred
    ? 'Vous avez vaincu le Tricorne&nbsp;: le mode infini vous attend.'
    : "Le mode infini vous attend&nbsp;: l'Abîme s'ouvre dans l'Arbre, la Forge et les Corrupted GachaDice en Boutique";

  // Compteurs entiers : séparateur de milliers français (et suffixes au-delà du
  // milliard), comme les sommes d'or.
  const count = (n: number): string => formatGold(n);
  const tile = (label: string, value: string, gold = false): string =>
    `<div class="f-stat${gold ? ' f-stat--gold' : ''} end-stat"><dt>${label}</dt><dd class="num">${value}</dd></div>`;

  const el = document.createElement('div');
  el.className = 'screen screen--end screen--victory screen--end-recap';
  el.innerHTML = `
    <div class="end-center">
      <header class="f-banner f-banner--win victory-banner">
        <h1 class="end-title">Le Tricorne est vaincu&nbsp;!</h1>
        <p class="end-lead victory-lead" data-role="victory-lead">${lead}</p>
      </header>
      <dl class="end-stats" data-role="victory-stats">
        ${tile('Temps de jeu', formatPlayClock(st.playSeconds))}
        ${tile('Runs', count(st.runs + 1))}
        ${tile('Or total', formatGold(st.totalGold), true)}
        ${tile('Ennemis tués', count(st.totalKills))}
        ${tile('Lancers', count(st.throws))}
        ${tile('Meilleur lancer', count(st.bestThrow))}
      </dl>
      <div class="victory-rank" data-role="victory-rank" hidden></div>
    </div>
    <div class="victory-actions">
      <button type="button" class="f-btn f-btn--ghost f-btn--block" data-action="credits">${icon('book')}<span>Crédits</span></button>
      <button type="button" class="f-btn f-btn--primary f-btn--block end-action" data-action="continue"><span>Continuer&nbsp;: mode infini</span>${icon('arrow')}</button>
    </div>
  `;
  // Récapitulatif des dégâts de l'Invasion gagnée, replié pour ne pas masquer
  // la victoire (comme `invasionWon`).
  el.insertBefore(buildDamageRecap(game, { open: false }), el.lastElementChild);
  container.appendChild(el);

  const continueBtn = el.querySelector<HTMLButtonElement>('[data-action="continue"]');
  const creditsBtn = el.querySelector<HTMLButtonElement>('[data-action="credits"]');

  let autoTimer: ReturnType<typeof setTimeout> | undefined;
  let cancelRoll: (() => void) | null = null;

  const setWaiting = (waiting: boolean): void => {
    if (!continueBtn) return;
    continueBtn.disabled = waiting;
    continueBtn.toggleAttribute('aria-busy', waiting);
  };

  const play = (): void => {
    if (autoTimer !== undefined) {
      clearTimeout(autoTimer);
      autoTimer = undefined;
    }
    cancelRoll?.();
    setWaiting(true);
    cancelRoll = rollCredits(ctx, {
      onDone: () => {
        cancelRoll = null;
        setWaiting(false);
      },
    });
  };

  if (!st.creditsSeen) {
    // Première victoire de la sauvegarde : les crédits s'enchaînent seuls.
    setWaiting(true);
    autoTimer = setTimeout(() => {
      autoTimer = undefined;
      play();
    }, CREDITS_AUTO_DELAY_MS);
  }

  // Rang au classement du chrono de campagne (phase 7, A87) : jamais bloquant,
  // la ligne se remplit quand la réponse arrive ; rien si les classements sont
  // indisponibles.
  const stopRank = mountRankLine(el.querySelector<HTMLElement>('[data-role="victory-rank"]'));

  const onContinue = (): void => game.acknowledgeVictory();
  continueBtn?.addEventListener('click', onContinue);
  creditsBtn?.addEventListener('click', play);

  return () => {
    stopRank();
    if (autoTimer !== undefined) clearTimeout(autoTimer);
    cancelRoll?.();
    continueBtn?.removeEventListener('click', onContinue);
    creditsBtn?.removeEventListener('click', play);
  };
};

/** Nouvelles lectures tant que le combat n'est pas arrivé au Worker. */
const RANK_RETRY_MS = 6_000;
const RANK_RETRIES = 3;

/**
 * Ligne « Chrono de campagne : 1 h 24 min · rang 7 sur 87 » de l'écran de
 * victoire. « Classement : envoi en cours » tant que le Worker n'a pas reçu le
 * combat (pas de `me`) ; un bouton « Entrer au classement » ouvre le dialogue
 * du pseudonyme quand le joueur n'en a pas. Renvoie la fonction d'arrêt.
 */
function mountRankLine(host: HTMLElement | null): () => void {
  let alive = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const api = host ? getLeaderboard() : null;
  if (!host || !api || !api.available()) return () => {};

  const show = (html: string): void => {
    if (!alive) return;
    host.innerHTML = html;
    host.hidden = html === '';
  };

  const load = (attempt: number): void => {
    if (!alive) return;
    if (!api.getPseudo()) {
      show(
        '<button type="button" class="f-btn f-btn--ghost f-btn--sm" data-action="join-leaderboard">Entrer au classement</button>',
      );
      host.querySelector('[data-action="join-leaderboard"]')?.addEventListener('click', () =>
        openPseudoDialog(() => load(0)),
      );
      return;
    }
    let pending: ReturnType<typeof api.fetchBoard>;
    try {
      pending = api.fetchBoard('chrono');
    } catch {
      return;
    }
    pending.then(
      (lb) => {
        if (!alive) return;
        const me = lb.me;
        if (me) {
          show(
            `<p class="victory-rank-line num" data-role="victory-rank-line">Chrono de campagne : ${formatChrono(me.value, false)} · rang ${me.rank} sur ${lb.total}</p>`,
          );
          return;
        }
        show('<p class="victory-rank-line" data-role="victory-rank-line">Classement : envoi en cours</p>');
        if (attempt < RANK_RETRIES) timer = setTimeout(() => load(attempt + 1), RANK_RETRY_MS);
      },
      () => {
        /* hors ligne ou en échec : rien à afficher (A79) */
        show('');
      },
    );
  };
  load(0);
  return () => {
    alive = false;
    if (timer !== undefined) clearTimeout(timer);
  };
}
